import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

// Load saved configuration from localStorage
const loadSavedConfig = () => {
  try {
    const savedConfig = localStorage.getItem('imqaConfig');
    return savedConfig ? JSON.parse(savedConfig) : null;
  } catch (error) {
    console.error('Error loading saved config:', error);
    return null;
  }
};

const defaultConfig = {
  collectorUrl: 'https://in-otel.imqa.io',
  serviceName: 'imqa-ie11-tester',
  serviceVersion: '1.0.0',
  serviceKey: 'test-service-key',
  debug: true,
  webVitals: true,
  consoleCapture: true,
  advancedNetworkCapture: true
};

const savedConfig = loadSavedConfig();

export default new Vuex.Store({
  state: {
    imqaInitialized: false,
    imqaConfig: savedConfig || defaultConfig,
    testResults: [],
    sessionId: null,
    initError: null
  },
  mutations: {
    setImqaInitialized(state, value) {
      state.imqaInitialized = value;
    },
    setInitError(state, error) {
      state.initError = error;
    },
    updateImqaConfig(state, config) {
      state.imqaConfig = { ...state.imqaConfig, ...config };
      // Save to localStorage
      try {
        localStorage.setItem('imqaConfig', JSON.stringify(state.imqaConfig));
      } catch (error) {
        console.error('Error saving config:', error);
      }
    },
    addTestResult(state, result) {
      state.testResults.unshift({
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...result
      });
      
      // Keep only the last 20 results
      if (state.testResults.length > 20) {
        state.testResults = state.testResults.slice(0, 20);
      }
    },
    setSessionId(state, id) {
      state.sessionId = id;
    },
    resetImqaConfig(state) {
      state.imqaConfig = { ...defaultConfig };
      state.imqaInitialized = false;
      state.sessionId = null;
      state.initError = null;
      // Clear localStorage
      localStorage.removeItem('imqaConfig');
    }
  },
  actions: {
    initializeIMQA({ commit, state }) {
      if (!window.IMQA) {
        const error = 'IMQA is not available';
        commit('setInitError', error);
        commit('addTestResult', { 
          type: 'error', 
          message: error
        });
        return false;
      }

      try {
        window.IMQA.init(state.imqaConfig);
        const sessionId = window.IMQA.getSessionId();
        commit('setImqaInitialized', true);
        commit('setInitError', null);
        commit('setSessionId', sessionId);
        commit('addTestResult', { 
          type: 'success', 
          message: 'IMQA initialized successfully',
          data: { sessionId, config: state.imqaConfig }
        });
        return true;
      } catch (error) {
        const errorMessage = error.message || 'Initialization failed';
        commit('setInitError', errorMessage);
        commit('setImqaInitialized', false);
        commit('addTestResult', { 
          type: 'error', 
          message: `IMQA initialization failed: ${errorMessage}`
        });
        return false;
      }
    },
    deinitializeIMQA({ commit }) {
      if (window.IMQA) {
        try {
          // Stop any active monitoring
          window.IMQA.stopSessionRecorder();
          // Reset the state
          commit('resetImqaConfig');
          commit('addTestResult', {
            type: 'info',
            message: 'IMQA deinitialized successfully'
          });
          return true;
        } catch (error) {
          const errorMessage = error.message || 'Deinitialization failed';
          commit('setInitError', errorMessage);
          commit('addTestResult', {
            type: 'error',
            message: `IMQA deinitialization failed: ${errorMessage}`
          });
          return false;
        }
      }
      return false;
    },
    runTest({ commit }, { type, name, fn }) {
      try {
        const result = fn();
        commit('addTestResult', { 
          type: 'success', 
          message: `${name} executed successfully`,
          data: result
        });
        return true;
      } catch (error) {
        commit('addTestResult', { 
          type: 'error', 
          message: `${name} failed: ${error.message}`,
          data: error.toString()
        });
        return false;
      }
    }
  }
});